import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";

import * as dayjs from 'dayjs';

import { inviteUser, updateUser, userDetails } from "../../services/users";
import { teamsList } from '../../services/teams';
import Switch from "../../components/shared/switch";
import { RemoveUserDialog } from "../../components/removeUserDialog";

const roles = ["coach", "assistant", "viewer", "player"];

const EditCoach = () => {
  const [user, setUser] = useState({ name: '', email: '', team_users_attributes: [] })
  const [teams, setTeams] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [errors, setErrors] = useState([])

  const [loading, setLoading] = useState(false)

  const { t } = useTranslation();

  let { userId, teamId } = useParams();
  const navigate = useNavigate();

  const changeTeamRole = (team_id, newRole) => {
    setUser(prevUser => {
      const updatedRoles = [...prevUser.team_users_attributes];
      const index = updatedRoles.findIndex(role => role.team_id === team_id);

      if (index !== -1) {
        updatedRoles[index] = { ...updatedRoles[index], role: newRole };
      }

      return { ...prevUser, team_users_attributes: updatedRoles };
    });
  }

  const getMyTeams = async () => {
    const { teams } = await teamsList();
    setTeams(teams);
  };

  useEffect(() => {
    getMyTeams();
  }, []);

  useEffect(() => {
    const selected = (user?.team_users_attributes || []).filter(tu => tu?._destroy != true).map(tu => tu?.team_id)
    const list = teams.filter(team => !selected.includes(team?.id))
    setAvailableTeams(list)
  }, [teams, user]);

  const navigateBack = () => {
    navigate(teamId ? `/club_admin/teams/${teamId}` : "/club_admin/users/")
  }

  const submit = async () => {
    const request = user?.id ? () => updateUser(user) : () => inviteUser({...user, team_id: teamId});
    setLoading(true)
    setErrors([])

    try {
      await request();
      navigateBack()
    } catch (e) {
      if (e?.response?.data?.errors) setErrors(e?.response?.data?.errors)
    }
    setLoading(false)
  }

  useEffect(() => {
    const getUser = async () => {
      const { user } = await userDetails(userId);
      setUser(user);
    };

    if (userId) getUser()
  }, [userId])

  const excludeFromTeam = (team_id) => {
    const index = user?.team_users_attributes?.findIndex(tu => tu.team_id === team_id)
    if (index === -1) return;

    if(!user.team_users_attributes[index]?.id) {
      setUser({...user, team_users_attributes: user.team_users_attributes.filter(tu => tu?.team_id !== team_id)})
    } else {
      setUser({...user, team_users_attributes: user.team_users_attributes.map(tu => tu.team_id === team_id ? {...tu, _destroy: true} : tu)})
    }
  }

  const addToTeam = (team_id) => {
    const index = user?.team_users_attributes?.findIndex(tu => tu.team_id === team_id)
    if (index === -1) {
      const team = availableTeams.find(t => t.id === team_id)
      setUser({...user, team_users_attributes: [...user.team_users_attributes, { role: roles[0], team_id, team }] })
    } else {
      setUser({...user, team_users_attributes: user.team_users_attributes.map(tu => tu.team_id === team_id ? {...tu, _destroy: false} : tu)})
    }
  }

  return (
    <div className="form-container user-form">
      <div className="form-container-header">
        <div className="form-container-header-text">{user?.id ? t('coaches.edit') : t('coaches.invite')} {t('coaches.coach')}</div>
      </div>
      { user?.id && <div className="form-container-info form-group ai-center flex1">
          <div className="form-container-info-text flex1">
            <div className='ellipsis'>{user?.name}</div>
            {user?.is_admin && <span className="admin-badge">{t('coaches.admin')}</span>}
          </div>

          <div className="form-container-info-text-secondary flex1">
            {t('coaches.user_created')} { dayjs(user?.created_at).format('YYYY-MM-DD')}
          </div>

          <RemoveUserDialog teamId={teamId} user={user} onClose={(reload) => {reload && navigateBack()}}/>
        </div>
      }
      <div className="form-container-area">
        <div className="form-container-area-group">
          <div className="form-container-area-group-full-width">
            <div className='form-group user-form '>
              <label htmlFor="name">{t('common.table_name')}</label>
              <input
                id="name"
                type="text"
                value={user.name}
                onChange={e => setUser({ ...user, name: e.target.value})}
              />
            </div>
          </div>

          <div className="form-container-area-group-full-width">
            <div className='form-group user-form '>
              <label htmlFor="email">{t('common.table_email')}</label>
                <input
                  id="email"
                  type="text"
                  value={user.email}
                  required
                  onChange={e => setUser({ ...user, email: e.target.value})}
                />
            </div>
          </div>
        </div>

        {user?.id && <div>
          {t('coaches.make_admin')}
          <div className="form-container-area-group make-admin">
            <div>{t('coaches.manage_club', { name: user?.name })}
              <Switch value={user?.is_admin} onChange={(value) => setUser({ ...user, is_admin: value })}/>
            </div>
            <div className="make-admin-notes">{t('coaches.text_muted')}</div>
          </div>
        </div> }

        <div className='teams-list-category'>
          <div className='teams-left-column'>
            <div className='teams-list-category-name'>{t('coaches.manage_teams')}</div>
            <div className='coach-teams-roles'>
              <div className="coach-teams-roles-row header">
                <div className="group-icon"></div>
                <div className="group-name">{t("common.table_name")}</div>
                <div className="group-role">{t("coaches.table_role")}</div>
                <div className="group-actions"></div>
              </div>

              {user?.team_users_attributes?.filter(tu => tu?._destroy != true)?.map(tu =>
                <div key={tu.team_id} className='coach-teams-roles-row '>
                  <div className="group-icon"></div>
                  <div className="group-name ellipsis">{tu?.team?.name}</div>
                  <div className='group-role'>
                    <select value={tu.role} className="form-control select"
                            onChange={(e) => changeTeamRole(tu.team_id, e.target.value)}>
                      {roles.map(role => <option value={role} key={role}>{t(`roles.${role}`)}</option>)}
                    </select>
                  </div>
                  <div className="group-actions">
                    <i className="icon icon-close pointer" onClick={() => excludeFromTeam(tu.team_id)}></i>
                  </div>
                 
                </div>
              )}
            </div>
            
          </div>
          <div className='teams-right-column'>
            <div className='teams-list-category-name'> {t("teams.all")}</div>
            <div className='coach-teams-roles'>
              <div className="coach-teams-roles-row all-teams header">
                <div className="group-icon"></div>
                <div className="group-name">{t("common.table_name")}</div>
                <div className="group-age">{t("teams.age")}</div>
                <div className="group-coach">{t("common.members")}</div>
                <div className="group-actions"></div>
              </div>

              {availableTeams?.map(team =>
                <div key={team.id} className='coach-teams-roles-row all-teams'>
                  <div className="group-icon"></div>
                  <div className="group-name ellipsis">{team?.name}</div>
                  <div className='group-age'>{team?.age}</div>
                  <div className='group-members'>{team?.users_count}</div>
                  <div className="group-actions">
                    <i className="icon icon-plus pointer" onClick={() => addToTeam(team.id)}></i>
                  </div>
                 
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-container-actions">
          <div className="flex-end">
            <button className="button" onClick={navigateBack} disabled={loading}>{t('buttons.common.cancel')}</button>
            <button className="button button-primary" onClick={submit}
                    disabled={loading}> {user?.id ?  t('buttons.common.update') : t('buttons.common.invite')} </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default memo(EditCoach);

const customStyles = {
  control: (provided, state) => ({ ...provided, border: '1px solid #eee', boxShadow: 'none', height: 45 }),
};